import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import InstitutionSearchInput, {InstitutionSearchInputInstitution} from 'institutions/InstitutionSearchInput';
import * as NewUserFlowActions from 'onboarding/new-user-flow/NewUserFlowActions';
import {SIGNUP_FLOW_V2_JOIN_ORGANIZATION_SCREEN} from 'onboarding/new-user-flow/NewUserFlowConstants';
import InfoSidePanel from 'onboarding/new-user-flow/signup-flow-v2/InfoSidePanel';
import {AcademicField, AcademicRole} from 'onboarding/new-user-flow/signup-flow-v2/SignupFlowConstants';
import SignupScreen from 'onboarding/new-user-flow/signup-flow-v2/SignupScreen';
import EntityStore from 'stores/EntityStore';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY_DARKER} from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';

function NextOptions(props: {shouldDisableNext: boolean; onSubmit: () => void}): JSX.Element {
  return (
    <div className={css(styles.nextOptions)}>
      <span
        className={classNames(css(styles.skip), 'u-pointer')}
        onClick={() => {
          analytics.track('onboarding_page', 'clicked', {
            page: 'academic_role_info',
            objectClicked: 'button',
            objectText: 'Skip for now',
            eventLastModified: 'FY22_Q2b3',
          });
          NewUserFlowActions.showScreen(SIGNUP_FLOW_V2_JOIN_ORGANIZATION_SCREEN);
        }}
      >
        Skip for now
      </span>
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        size={ButtonSize.MEDIUM}
        text="Next"
        onClick={props.onSubmit}
        disabled={props.shouldDisableNext}
      />
    </div>
  );
}

interface AcademicDropdownProps<T> {
  onChange: (newValue: T | null) => void;
  placeholder: string;
  values: Array<T>;
  selectedValue: T | null;
}

type AcademicDropdownValue = AcademicField | AcademicRole;

function AcademicDropdown<T extends AcademicDropdownValue>(props: AcademicDropdownProps<T>): JSX.Element {
  const optionDatas: Array<OptionData> = props.values.map((value: T) => ({
    name: value,
    value,
  }));
  const options = optionDatas.map(({name, value}) => (
    <Option
      key={value}
      onSelect={() => {
        props.onChange(value);
      }}
      value={value}
    >
      {name}
    </Option>
  ));

  return (
    <div className="u-position-relative">
      {props.selectedValue && (
        <BenchlingIcon
          className={css(styles.dropdownClearIcon)}
          icon="close"
          onClick={() => {
            props.onChange(null);
          }}
        />
      )}
      <SelectDropdown
        buttonClassName={classNames(props.selectedValue == null && 'u-faded', css(styles.dropdown))}
        menu={
          <Menu height={MenuHeight.MEDIUM} width={MenuWidth.FULL}>
            {options}
          </Menu>
        }
        displayName={props.selectedValue ?? props.placeholder}
        alwaysDropDown={true}
      />
    </div>
  );
}

interface InstitutionDropdownProps {
  onChange: (institution: InstitutionSearchInputInstitution | null) => void;
  placeholder: string;
  selectedValueDisplayString?: string;
}

function InstitutionDropdown(props: InstitutionDropdownProps): JSX.Element {
  return (
    <div className={css(styles.institutionSearchInput)}>
      {props.selectedValueDisplayString && (
        <BenchlingIcon
          className={css(styles.dropdownClearIcon)}
          icon="close"
          onClick={() => {
            props.onChange(null);
          }}
        />
      )}
      <SelectDropdown
        buttonClassName={classNames(
          props.selectedValueDisplayString == null && 'u-faded',
          css(styles.dropdown)
        )}
        displayName={props.selectedValueDisplayString ?? props.placeholder}
        menu={<InstitutionSearchInput onSelect={props.onChange} />}
      />
    </div>
  );
}

const handleSubmitRoleInfo = (
  institutionId: string | null,
  role: string | null,
  field: string | null
): void => {
  analytics.track('onboarding_page', 'clicked', {
    page: 'academic_role_info',
    objectClicked: 'button',
    objectText: 'Next',
    eventLastModified: 'FY22_Q2b3',
    role,
    scientificField: field,
  });
  NewUserFlowActions.submitAcademicRoleInfo(
    {
      institutionId,
      role,
      field,
    },
    () => {
      NewUserFlowActions.showScreen(SIGNUP_FLOW_V2_JOIN_ORGANIZATION_SCREEN);
    }
  );
};

function AcademicRoleInfoEditor(): JSX.Element {
  const [institution, setInstitution] = React.useState<InstitutionSearchInputInstitution | null>(
    EntityStore.currentUser().institution
  );
  const [role, setRole] = React.useState<AcademicRole | null>(null);
  const [field, setField] = React.useState<AcademicField | null>(null);

  const handleChangeRole = (newRole: AcademicRole | null): void => {
    setRole(newRole);
  };
  const handleChangeField = (newField: AcademicField | null): void => {
    setField(newField);
  };
  const handleChangeInstitution = (institution: InstitutionSearchInputInstitution | null): void => {
    setInstitution(institution);
  };

  return (
    <>
      <InstitutionDropdown
        selectedValueDisplayString={institution?.fullName}
        placeholder="What’s your institution?"
        onChange={handleChangeInstitution}
      />
      <AcademicDropdown
        onChange={handleChangeRole}
        placeholder="What's your current role?"
        values={Object.values(AcademicRole)}
        selectedValue={role}
      />
      <AcademicDropdown
        onChange={handleChangeField}
        placeholder="What scientific field do you work in?"
        values={Object.values(AcademicField)}
        selectedValue={field}
      />
      <NextOptions
        shouldDisableNext={institution == null && role == null && field == null}
        onSubmit={() => {
          handleSubmitRoleInfo(institution?.id ?? null, role, field);
        }}
      />
    </>
  );
}

export default function AcademicRoleInfoScreen(): JSX.Element {
  const titleText = 'Now let’s build out your profile';
  const bodyText = 'Benchling is designed for academic scientists like you.';
  return (
    <SignupScreen>
      <Row className={css(styles.row)}>
        <Col xs={4} sm={6}>
          <InfoSidePanel titleText={titleText} bodyText={bodyText} />
        </Col>
        <Col xs={8} sm={6}>
          <AcademicRoleInfoEditor />
        </Col>
      </Row>
    </SignupScreen>
  );
}

const styles = StyleSheet.create({
  row: {
    marginLeft: '52px',
    marginRight: '52px',
  },
  mediumText: {
    fontSize: '18px',
    lineHeight: '24px',
    marginBottom: '11px',
  },
  dropdown: {
    position: 'relative',
    minHeight: '40px',
    marginTop: '8px',
  },
  nextOptions: {
    marginTop: '118px',
    float: 'right',
  },
  skip: {
    marginTop: '134px',
    fontSize: '13px',
    marginRight: '22px',
    color: BLUE_LIGHT,
  },
  institutionSearchInput: {
    position: 'relative',
    marginTop: '120px',
  },
  dropdownClearIcon: {
    position: 'absolute',
    top: '24px',
    float: 'right',
    right: '30px',
    fontSize: '9px',
    cursor: 'pointer',
    color: GRAY_DARKER,
    zIndex: 1,
  },
});
